import React from "react";
import { Pressable, StyleSheet, Text as RNText } from "react-native";
import { radius } from "~/styles/tokens";
import { useTheme, type ThemePreference } from "~/styles/theme";

type ThemeToggleProps = {
  /** Called after the theme changes so the view layer can persist the choice
   *  (e.g. to the user's profile). The primitive only updates the context. */
  onPreferenceChange?: (preference: ThemePreference) => void;
};

/** Compact control that cycles light → dark → system. */
export const ThemeToggle: React.FC<ThemeToggleProps> = ({
  onPreferenceChange,
}) => {
  const { preference, scheme, colors, setPreference } = useTheme();

  const glyph =
    preference === "system" ? "🌗" : preference === "dark" ? "🌙" : "☀️";

  const cycle = () => {
    const next: ThemePreference =
      preference === "light"
        ? "dark"
        : preference === "dark"
          ? "system"
          : "light";
    setPreference(next);
    onPreferenceChange?.(next);
  };

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`Theme: ${preference} (currently ${scheme}). Tap to change.`}
      onPress={cycle}
      style={({ pressed }) => [
        styles.button,
        {
          borderColor: colors.border,
          backgroundColor: pressed ? colors.surfaceMuted : colors.surface,
        },
      ]}
    >
      <RNText style={styles.glyph}>{glyph}</RNText>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  button: {
    width: 40,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderRadius: radius.full,
  },
  glyph: {
    fontSize: 18,
  },
});
